import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal rather
// than importing play_dialog.ts, same leaf-module reasoning as
// block_showcase.ts's local PIECE map.
const COMMON_DIFFICULTY = 0;

const PIECE = {
  Cube: 1,
  Stone: 2,
} as const;

// Mirrors BarrierHead / BarrierMount in entities/barrier.ts. Same reasoning as
// PIECE above: this file stays a leaf that only the debug menu pulls in.
const HEAD = { Blade: 0, Hammer: 1 } as const;
const MOUNT = { Ceiling: 1, Left: 2, Right: 3 } as const;

const COLS = 9;
const ROWS = 10;

interface PieceSpec
{
  type: number;
  x: number;
  /** Defaults to standing on the tabletop. */
  y?: number;
  width: number;
  height: number;
  variant?: number;
}

function makeLayer( specs: PieceSpec[], cols = COLS ): LevelLayer
{
  const grid = Array.from( { length: ROWS }, () => Array( cols ).fill( 0 ) );
  const pieces: LevelPiece[] = [];

  specs.forEach( ( spec, index ) =>
  {
    const id = index + 1;
    const y = spec.y ?? ROWS - spec.height;

    pieces.push( {
      id,
      type: spec.type as LevelPieceType,
      x: spec.x,
      y,
      width: spec.width,
      height: spec.height,
      variant: spec.variant,
    } );

    for ( let row = y; row < y + spec.height; row++ )
      for ( let col = spec.x; col < spec.x + spec.width; col++ )
        grid[row][col] = id;
  } );

  return { grid, pieces };
}

/** A full-height wall for comparing barrier and block lighting directly. */
function targetWall(): LevelLayer[]
{
  const specs: PieceSpec[] = [];
  for ( let y = 0; y < ROWS; y++ )
    for ( let x = 0; x < COLS; x++ )
      specs.push( {
        type: PIECE.Cube,
        x,
        y,
        width: 1,
        height: 1,
        variant: ( x + y ) % 2 === 0 ? 0 : 2,
      } );

  return [ makeLayer( specs ) ];
}

/** Rows of 7, 5, 3 and 1 cubes centred on the table, a stone crowning it. */
function pyramid(): LevelLayer
{
  const specs: PieceSpec[] = [];
  for ( let step = 0; step < 4; step++ )
  {
    const y = ROWS - 1 - step;
    for ( let x = 1 + step; x <= COLS - 2 - step; x++ )
      specs.push( {
        type: step === 3 ? PIECE.Stone : PIECE.Cube,
        x,
        y,
        width: 1,
        height: 1,
        variant: step === 3 ? undefined : ( x + step ) % 3,
      } );
  }

  return makeLayer( specs );
}

/** Rows of 3, 2 and 1 cubes on a 3-column table — small enough for two tables. */
function smallPyramid(): LevelLayer
{
  const cols = 3;
  const specs: PieceSpec[] = [];
  for ( let step = 0; step < 3; step++ )
    for ( let x = 0; x < cols - step; x++ )
      specs.push( {
        type: PIECE.Cube,
        x: x + ( step === 2 ? 1 : 0 ),
        y: ROWS - 1 - step,
        width: 1,
        height: 1,
        variant: ( x + step ) % 3,
      } );

  return makeLayer( specs, cols );
}

/**
 * Barrier coordinates are in grid cells, measured from the table: `x` across
 * the frame from its middle, `y` up from the tabletop, `z` forwards from its
 * front edge. Reach worth planning around: a propeller sweeps a circle of about
 * 2.25 cells around its bearing, and a hammer swings 3 cells out from its own.
 *
 * Every level needs blocks on the table: the game ends the moment no entity is
 * left alive (GameScreen#checkGameEnd), and barriers are not entities — a
 * barrier-only level would be won before the first shot.
 */
const BARRIER_LEVELS: LevelConfig[] = [
  {
    // Propeller on a rod reaching in from the left edge of the frame.
    level: 90500,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -1.5, y: 2.5 },
    ],
  },
  {
    // Hammer hanging out of the sky — the silhouette from the reference art.
    level: 90501,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 5 },
    ],
  },
  {
    // The other combinations: a hammer on a rod from the right edge, swinging
    // up and down with its head towards the middle, and a propeller on a
    // pillar dropped from above. The hammer stands a cell nearer the camera so
    // its swing clears the propeller's circle in depth.
    level: 90502,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Hammer, mount: MOUNT.Right, x: 3, y: 3.5, z: 2 },
      { head: HEAD.Blade, mount: MOUNT.Ceiling, x: -2.5, y: 3 },
    ],
  },
  {
    // A row of them, the way a real level would use barriers: a moving screen
    // the ball has to find a gap in. Spaced 3.5 cells apart so the propellers'
    // circles do not overlap.
    level: 90503,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: targetWall(),
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -3.5, y: 2.5 },
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 0, y: 5, angle: 30 },
      { head: HEAD.Blade, mount: MOUNT.Right, x: 3.5, y: 2.5 },
    ],
  },
  {
    // A playable level rather than a wall: a two-deep pyramid the ball can
    // actually knock over, guarded by a propeller on the left and a hammer
    // swinging over the right. The hammer stands a cell nearer the camera so
    // its swing clears the propeller's circle in depth.
    level: 90504,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ pyramid(), pyramid() ],
    barriers: [
      { head: HEAD.Blade, mount: MOUNT.Left, x: -3, y: 2.5 },
      { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 3, y: 6.5, z: 2 },
    ],
  },
  {
    // Barriers on a multi-table level: each table carries its own, positioned
    // from that table rather than from tables[0] or the frame. The left table
    // gets a propeller and a hammer, the right one a single propeller.
    level: 90505,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    tables: [
      {
        slot: { u: -0.8, v: 0 },
        layers: [ smallPyramid() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Left, x: -2, y: 2.5 },
          { head: HEAD.Hammer, mount: MOUNT.Ceiling, x: 1.5, y: 6 },
        ],
      },
      {
        slot: { u: 0.8, v: 0 },
        layers: [ smallPyramid() ],
        barriers: [
          { head: HEAD.Blade, mount: MOUNT.Right, x: 0, y: 2.5 },
        ],
      },
    ],
  },
];

export class BarrierShowcase
{
  public static readonly TESTS = [
    { label: "BLADE on a LEFT rod", level: 90500 },
    { label: "HAMMER out of the sky", level: 90501 },
    { label: "RIGHT hammer + SKY blade", level: 90502 },
    { label: "ROW of barriers", level: 90503 },
    { label: "PYRAMID guarded by BLADE + HAMMER", level: 90504 },
    { label: "TWO TABLES, barriers per table", level: 90505 },
  ] as const;

  private static active = false;
  private static index = 0;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.index = ( index + this.TESTS.length ) % this.TESTS.length;
    return this.TESTS[this.index].level;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static currentLevel(): number
  {
    return this.TESTS[this.index].level;
  }

  public static advance(): number
  {
    return this.activate( this.index + 1 );
  }

  public static isShowcaseLevel( level: number ): boolean
  {
    return this.TESTS.some( test => test.level === level );
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return BARRIER_LEVELS.find( config => config.level === level ) ?? null;
  }
}
